import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { LivePointId, UserIdSchema } from "../id"
import { CreatedAt, UpdatedAt } from "./system"
import { Period, PeriodState } from "./common"

/**
 * LIVEポイント台帳エントリの種別。
 * 追記のみのイベントログとして、LIVEポイントの全ての増減を記録する。
 */
export const LivePointKind = z.enum([
  "grant",
  "exchange-to-dotmoney",
  "exchange-to-coin",
  "expire",
  "admin-update",
])

export type LivePointKind = z.infer<typeof LivePointKind>

/**
 * LIVEポイント付与時に適用されたレートの由来。
 * "campaign" は仕様凍結待ちのため当面は書き込まれない。
 */
export const LivePointRateSource = z.enum([
  "standard",
  "user-specific",
  "campaign",
])

export type LivePointRateSource = z.infer<typeof LivePointRateSource>

/**
 * FIFO消費時に、どの grant からいくら消費したかを記録する。
 */
export const LivePointConsumedGrant = z.object({
  grant_id: z.string().describe("消費元の grant レコードの ID"),
  amount: z.number().int().describe("この grant から消費した LIVEポイント量（正の整数）"),
})

export type LivePointConsumedGrant = z.infer<typeof LivePointConsumedGrant>

/**
 * LIVEポイント台帳ドキュメント。
 * root レベル live-points/{auto-id} に格納される。
 *
 * kind による discriminated union:
 * - "grant": remaining, grant_period, grant_period_state, audit snapshot フィールドが存在
 * - "exchange-to-dotmoney" | "exchange-to-coin": consumed_grants が存在
 * - "expire": expired_grant_id が存在
 * - "admin-update": reason, admin_user_id, remaining, grant_period, grant_period_state が存在
 */
export const LivePointDocumentMeta = z.object({
  kind: LivePointKind,
  user_id: UserIdSchema.describe("対象ユーザーの UID"),
  amount: z.number().int().describe("LIVEポイント変動量。付与は正、消費・失効は負"),
  balance_after: z.number().int().describe("この操作後の LIVEポイント残高"),

  // kind == "grant" | "admin-update"（加算時）
  remaining: z.number().int().nullable().optional().describe("この grant から未消費の残り LIVEポイント。null は消費対象外（期限切れ等）"),
  grant_period: Period.optional().describe("LIVEポイントの有効期間。ドットマネー連携時にまとめて150日後に設定予定"),
  grant_period_state: PeriodState.optional().describe("grant_period の算出状態。daemon が更新する"),

  // kind == "grant" の audit snapshot（退会後もこのレコード単独で計算根拠を再現可能にする）
  source_live_id: z.string().optional().describe("由来の LIVE ID"),
  power_consumed: z.number().int().optional().describe("有償スコープの coin_spent 合計（snapshot）"),
  applied_conversion_rate: z.number().int().optional().describe("適用レート分子（例: 4401）"),
  applied_conversion_rate_unit: z.number().int().optional().describe("適用レート分母（例: 10000）"),
  applied_rate_source: LivePointRateSource.optional().describe("どのレート config が選ばれたか"),

  // kind == "exchange-to-dotmoney" | "exchange-to-coin" のみ
  consumed_grants: z.array(LivePointConsumedGrant).optional().describe("FIFO 消費で使用した grant のリスト"),

  applied_exchange_rate: z.number().int().optional().describe("適用された交換レート分子"),
  applied_exchange_rate_unit: z.number().int().optional().describe("適用された交換レート分母"),
  free_coin_amount: z.number().int().optional().describe("LIVEポイント→無償コイン交換で付与されたコイン数"),

  // kind == "expire" のみ
  expired_grant_id: z.string().optional().describe("失効した grant レコードの ID"),

  // kind == "admin-update" のみ
  reason: z.string().optional().describe("管理者による調整理由"),
  admin_user_id: z.string().optional().describe("操作を行った管理者の UID"),
})

export type LivePointDocumentMeta = z.infer<typeof LivePointDocumentMeta>

export const LivePointDocumentBody = z.intersection(
  LivePointDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type LivePointDocumentBody = z.infer<typeof LivePointDocumentBody>

export type LivePointDocument = LivePointDocumentBody & {
  id: LivePointId
}

/**
 * 管理者による LIVEポイント加減算リクエスト。
 * amount が正なら加算（grant 作成）、負なら減算（FIFO 消費）。
 */
export const UpdateLivePointRequest = z.object({
  amount: z.number().int().describe("変動量。正=加算、負=減算。0 は不可"),
  reason: z.string().min(1).describe("調整理由（必須）"),
})

export type UpdateLivePointRequest = z.infer<typeof UpdateLivePointRequest>

/**
 * LIVEポイント交換リクエスト（クライアントAPI用）。
 * kind により交換先を指定する。
 */
export const CreateLivePointExchangeRequest = z.object({
  kind: z.literal("free-coin").describe("交換先。現在は free-coin のみ対応"),
  amount: z.number().int().min(1).describe("交換する LIVEポイント量（正の整数）"),
})

export type CreateLivePointExchangeRequest = z.infer<typeof CreateLivePointExchangeRequest>

/**
 * 管理者による LIVEポイント交換停止/解除リクエスト。
 */
export const UpdateLivePointExchangeBlockRequest = z.object({
  blocked: z.boolean().describe("true で交換停止、false で解除"),
  reason: z.string().min(1).describe("停止/解除理由（必須）"),
})

export type UpdateLivePointExchangeBlockRequest = z.infer<typeof UpdateLivePointExchangeBlockRequest>

export class LivePointDocuments {

  static toDocumentData(doc: LivePointDocumentBody): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): LivePointDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: LivePointId(doc.id),
      kind: data["kind"],
      user_id: data["user_id"],
      amount: data["amount"],
      balance_after: data["balance_after"],
      remaining: data["remaining"],
      grant_period: data["grant_period"],
      grant_period_state: data["grant_period_state"],
      source_live_id: data["source_live_id"],
      power_consumed: data["power_consumed"],
      applied_conversion_rate: data["applied_conversion_rate"],
      applied_conversion_rate_unit: data["applied_conversion_rate_unit"],
      applied_rate_source: data["applied_rate_source"],
      consumed_grants: data["consumed_grants"],
      applied_exchange_rate: data["applied_exchange_rate"],
      applied_exchange_rate_unit: data["applied_exchange_rate_unit"],
      free_coin_amount: data["free_coin_amount"],
      expired_grant_id: data["expired_grant_id"],
      reason: data["reason"],
      admin_user_id: data["admin_user_id"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
